import { CSSProperties, FC, ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";

export interface iToastDeckItem {
	key: string;
	node: ReactNode;
}

interface iToastDeckProps {
	items: iToastDeckItem[];
	expanded: boolean;
	onExpandedChange: (expanded: boolean) => void;
}

interface iToastDeckItemProps {
	itemKey: string;
	className: string;
	style: CSSProperties;
	onHeight: (key: string, height: number) => void;
	children: ReactNode;
}

const PEEK = 14;
const GAP = 8;
const SCALE_STEP = 0.05;
const VISIBLE = 3;

const ToastDeckItem: FC<iToastDeckItemProps> = ({ itemKey, className, style, onHeight, children }) => {
	const ref = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return undefined;
		const report = () => onHeight(itemKey, element.offsetHeight);
		report();
		const observer = new ResizeObserver(report);
		observer.observe(element);
		return () => observer.disconnect();
	}, [itemKey, onHeight]);

	return (
		<div ref={ref} className={className} style={style}>
			{children}
		</div>
	);
};

const ToastDeck: FC<iToastDeckProps> = ({ items, expanded, onExpandedChange }) => {
	const [heights, setHeights] = useState<Record<string, number>>({});

	const onHeight = useCallback(
		(key: string, height: number) =>
			setHeights((current) => (current[key] === height ? current : { ...current, [key]: height })),
		[],
	);

	const frontHeight = heights[items[0]?.key] ?? 0;
	const offsets = items.reduce<number[]>((acc, item, index) => {
		acc.push(index === 0 ? 0 : acc[index - 1] + (heights[items[index - 1].key] ?? 0) + GAP);
		return acc;
	}, []);
	const lastIndex = items.length - 1;
	const height = expanded
		? (offsets[lastIndex] ?? 0) + (heights[items[lastIndex]?.key] ?? 0)
		: frontHeight + (Math.min(items.length, VISIBLE) - 1) * PEEK;

	return (
		<div
			className={`ycToastStack ${expanded ? "ycToastStack--expanded" : ""}`}
			style={{ height: items.length ? height : 0 }}
			onMouseEnter={() => onExpandedChange(true)}
			onMouseLeave={() => onExpandedChange(false)}
		>
			{items.map((item, index) => {
				const behind = !expanded && index > 0;
				const ownHeight = heights[item.key] ?? frontHeight;
				return (
					<ToastDeckItem
						key={item.key}
						itemKey={item.key}
						onHeight={onHeight}
						className={`ycToastStack__item ${behind ? "ycToastStack__item--behind" : ""}`}
						style={{
							zIndex: items.length - index,
							opacity: index >= VISIBLE && !expanded ? 0 : 1,
							transform: expanded
								? `translateY(${offsets[index]}px)`
								: `translateY(${frontHeight - ownHeight + index * PEEK}px) scale(${1 - index * SCALE_STEP})`,
							clipPath:
								behind && ownHeight > frontHeight ? `inset(${ownHeight - frontHeight}px 0 0 0)` : undefined,
						}}
					>
						{item.node}
					</ToastDeckItem>
				);
			})}
		</div>
	);
};

export default ToastDeck;
